import { Injectable, signal, computed } from '@angular/core';
import {ShipmentService} from './shipment.service';

export enum LoadingStates {
  UNKNOWN = "UNKNOWN",
  REGISTER = "REGISTER",
  LOGIN = "LOGIN",
  REQUEST_PASSWORD = "REQUEST_PASSWORD",
  RESTORE_PASSWORD = "RESTORE_PASSWORD",
  CHECK_PASSWORD_LINK = "CHECK_PASSWORD_LINK",
  COMPANIES_GET = "COMPANIES_GET",
  COMPANIES_GET_ITEM = "COMPANIES_GET_ITEM",
  COMPANIES_CREATE_EDIT = "COMPANIES_CREATE_EDIT",
  COMPANIES_DELETE = "COMPANIES_DELETE",
  COMPANIES_UPLOAD_FILES = "COMPANIES_UPLOAD_FILES",
  COMPANIES_UPLOAD_FACSIMILE = "COMPANIES_UPLOAD_FACSIMILE",
  COMPANIES_UPLOAD_SIGNATURE = "COMPANIES_UPLOAD_SIGNATURE",
  COMPANIES_DOWNLOAD_FILE = "COMPANIES_DOWNLOAD_FILE",
  GET_USER_TYPE = "GET_USER_TYPE",
  EDIT_USER_TYPE = "EDIT_USER_TYPE",
  WAREHOUSE_CREATE_EDIT = "WAREHOUSE_CREATE_EDIT",
  WAREHOUSE_DELETE = "WAREHOUSE_DELETE",
  WAREHOUSE_GET = "WAREHOUSE_GET",
  WAREHOUSE_GET_ITEM = "WAREHOUSE_GET_ITEM",
  WAREHOUSE_DOWNLOAD_FILE = "WAREHOUSE_DOWNLOAD_FILE",
  WAREHOUSE_BRIEF_GET = "WAREHOUSE_BRIEF_GET",
  GET_IMAGES = "GET_IMAGES",
  WAREHOUSE_UPLOAD_IMAGES = "WAREHOUSE_UPLOAD_IMAGES",
  WAREHOUSE_UPLOAD_FILES = "WAREHOUSE_UPLOAD_FILES",
  PRODUCT_GET = "PRODUCT_GET",
  PRODUCT_GET_ITEM = "PRODUCT_GET_ITEM",
  PRODUCT_CREATE_EDIT = "PRODUCT_CREATE_EDIT",
  PRODUCT_UPLOAD_IMAGES = "PRODUCT_UPLOAD_IMAGES",
  CATEGORIES_GET = "CATEGORIES_GET",
  MARKETPLACES_GET = "MARKETPLACES_GET",
  ATTRIBUTES_GET = "ATTRIBUTES_GET",
  SHIPMENT_GET_WAREHOUSE = "SHIPMENT_GET_WAREHOUSE",
  SHIPMENT_ADD_WAREHOUSE = "SHIPMENT_ADD_WAREHOUSE",
  SHIPMENT_GET = "SHIPMENT_GET",
  SHIPMENT_GET_ITEM = "SHIPMENT_GET_ITEM",
  SHIPMENT_CREATE_EDIT = "SHIPMENT_CREATE_EDIT",
  SHIPMENT_PUBLISH = "SHIPMENT_PUBLISH",
  SHIPMENT_AWAITING_RECOUNT_GET = "SHIPMENT_AWAITING_RECOUNT_GET",
  SHIPMENT_REVOKE = "SHIPMENT_REVOKE",
  SHIPMENT_DECLINE = "SHIPMENT_DECLINE",
  BRIEF_CREATE_EDIT = "BRIEF_CREATE_EDIT",
  BRIEF_GET_ITEM = "BRIEF_GET_ITEM",
  BRIEF_GET = "BRIEF_GET",
  BRIEF_DELETE = "BRIEF_DELETE",
  BRIEF_CLIENTS_GET = "BRIEF_CLIENTS_GET",
  BRIEF_FINISH = "BRIEF_FINISH",
  BRIEF_DELIVERY_EMPLOYEES_GET = "BRIEF_DELIVERY_EMPLOYEES_GET",
  NOMENCLATURE_DELETE = "NOMENCLATURE_DELETE",
  NOMENCLATURE_CREATE_EDIT = "NOMENCLATURE_CREATE_EDIT",
  NOMENCLATURE_GET = "NOMENCLATURE_GET",
  NOMENCLATURE_GET_ITEM = "NOMENCLATURE_GET_ITEM",
  WORKS_DELETE = "WORKS_DELETE",
  WORKS_CREATE_EDIT = "WORKS_CREATE_EDIT",
  WORKS_GET = "WORKS_GET",
  WORKS_GET_ITEM = "WORKS_GET_ITEM",
  GET_ME = "GET_ME",
  STAFF_CREATE_EDIT = "STAFF_CREATE_EDIT",
  STAFF_GET = "STAFF_GET",
  STAFF_GET_ITEM = "STAFF_GET_ITEM",
  ROLE_CREATE_EDIT = "ROLE_CREATE_EDIT",
  ROLE_DELETE = "ROLE_DELETE",
  ROLE_GET = "ROLE_GET",
  ROLE_GET_ITEM = "ROLE_GET_ITEM",
  STAFF_BLOCK = "STAFF_BLOCK",
  STAFF_DELETE = "STAFF_DELETE",
  STAFF_PASSWORD_RESET = "STAFF_PASSWORD_RESET",
  SIGNED_GET = "SIGNED_GET",
  SIGN_CHANGE = "SIGN_CHANGE",
  PRODUCT_CREATE_INTEGRATION = "PRODUCT_CREATE_INTEGRATION",
  PRODUCT_GET_INTEGRATION = "PRODUCT_GET_INTEGRATION",
  PRODUCT_DELETE_INTEGRATION = "PRODUCT_DELETE_INTEGRATION",
  SELLERS_CREATE_EDIT = "SELLERS_CREATE_EDIT",
  SELLERS_DELETE = "SELLERS_DELETE",
  SELLERS_GET = "SELLERS_GET",
  SELLERS_GET_ITEM = "SELLERS_GET_ITEM",
  CHATS_WS_CONNECT = "CHATS_WS_CONNECT",
  CHATS_GET_ALL = "CHATS_GET_ALL",
  CHATS_UNREAD_MESSAGES = "CHATS_UNREAD_MESSAGES",
  CHATS_GET_BRIEFS = "CHATS_GET_BRIEFS",
  CHATS_GET_ITEM = "CHATS_GET_ITEM",
  CHATS_GET_ID = "CHATS_GET_ID",
  MESSAGE_DOWNLOAD_FILE = "MESSAGE_DOWNLOAD_FILE",
  BLOGGER_GET = "BLOGGER_GET",
}

export interface LoadingItem {
  id: string;
  type: LoadingStates;
}

@Injectable({
  providedIn: "root",
})
export class LoadingService {
  readonly StatesNames = LoadingStates;

  // Используем Map для более эффективного доступа по ключу
  private loadingStatesMap = signal<Map<LoadingStates, boolean>>(
    new Map(Object.values(LoadingStates).map(state => [state, false]))
  );

  // Храним только активные загрузки
  private activeLoadingsSignal = signal<LoadingItem[]>([]);

  // Публичные API
  // @ts-ignore
  readonly stateIsActive = computed((state: LoadingStates) =>
    this.loadingStatesMap().get(state) || false
  );

  readonly isAnyLoading = computed(() => this.activeLoadingsSignal().length > 0);

  // Метод для проверки конкретного состояния
  isStateLoading(state: LoadingStates): boolean {
    return this.loadingStatesMap().get(state) || false;
  }

  // Метод для проверки нескольких состояний (любое из них активно)
  isAnyStateLoading(states: LoadingStates[]): boolean {
    return states.some(state => this.isStateLoading(state));
  }

  // Добавить загрузку и вернуть её ID
  startLoading(requestType = LoadingStates.UNKNOWN): string {
    const id = this.generateUUID();

    this.activeLoadingsSignal.update(loadings => [
      ...loadings,
      { id, type: requestType }
    ]);

    this.updateStateValue(requestType, true);

    return id;
  }

  // Завершить загрузку по ID
  finishLoading(id: string): void {
    const loadingItem = this.activeLoadingsSignal().find(item => item.id === id);

    if (!loadingItem) return;

    const type = loadingItem.type;

    // Удаляем загрузку
    this.activeLoadingsSignal.update(loadings =>
      loadings.filter(item => item.id !== id)
    );

    // Проверяем, остались ли другие загрузки этого типа
    const hasMoreOfSameType = this.activeLoadingsSignal().some(item => item.type === type);

    if (!hasMoreOfSameType) {
      this.updateStateValue(type, false);
    }
  }

  // Метод для прямого получения значения состояния
  getStateValue(state: LoadingStates): boolean {
    return this.loadingStatesMap().get(state) || false;
  }

  // Вспомогательные методы
  private updateStateValue(state: LoadingStates, value: boolean): void {
    this.loadingStatesMap.update(map => {
      const newMap = new Map(map);
      newMap.set(state, value);
      return newMap;
    });
  }

  private generateUUID(): string {
    return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, (c) => {
      const r = (Math.random() * 16) | 0;
      const v = c === "x" ? r : (r & 0x3) | 0x8;
      return v.toString(16);
    });
  }
}
